import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { GameType, LeaderboardPeriod } from '@shark-games/shared';

const gameTypes: { id: GameType; name: string }[] = [
  { id: 'spades', name: 'Spades' },
  { id: 'bid_whist', name: 'Bid Whist' },
  { id: 'tonk', name: 'Tonk' },
  { id: 'dominoes_big6', name: 'Dominoes (Big 6)' },
];

const periods: { id: LeaderboardPeriod; name: string }[] = [
  { id: 'all_time', name: 'All Time' },
  { id: 'monthly', name: 'Monthly' },
  { id: 'weekly', name: 'Weekly' },
  { id: 'daily', name: 'Daily' },
];

export default function LeaderboardPage() {
  const [selectedGame, setSelectedGame] = useState<GameType>('spades');
  const [selectedPeriod, setSelectedPeriod] = useState<LeaderboardPeriod>('all_time');

  const { data, isLoading } = useQuery({
    queryKey: ['leaderboard', selectedGame, selectedPeriod],
    queryFn: async () => {
      const response = await api.get(`/leaderboards/${selectedGame}/${selectedPeriod}`);
      return response.data.data;
    },
  });

  return (
    <div className="min-h-screen py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-8">Leaderboards</h1>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <div className="flex bg-shark-800 rounded-lg p-1">
            {gameTypes.map((game) => (
              <button
                key={game.id}
                onClick={() => setSelectedGame(game.id)}
                className={`px-4 py-2 rounded-lg transition ${
                  selectedGame === game.id
                    ? 'bg-shark-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {game.name}
              </button>
            ))}
          </div>
          <div className="flex bg-shark-800 rounded-lg p-1">
            {periods.map((period) => (
              <button
                key={period.id}
                onClick={() => setSelectedPeriod(period.id)}
                className={`px-4 py-2 rounded-lg transition ${
                  selectedPeriod === period.id
                    ? 'bg-shark-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {period.name}
              </button>
            ))}
          </div>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-shark-800 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shark-400 mx-auto"></div>
            </div>
          ) : data?.entries?.length > 0 ? (
            <table className="w-full">
              <thead className="bg-shark-700">
                <tr>
                  <th className="px-6 py-4 text-left">Rank</th>
                  <th className="px-6 py-4 text-left">Player</th>
                  <th className="px-6 py-4 text-center">Games</th>
                  <th className="px-6 py-4 text-center">Wins</th>
                  <th className="px-6 py-4 text-right">ELO</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry: {
                  user_id: string;
                  rank: number;
                  display_name: string;
                  username: string;
                  games_played: number;
                  games_won: number;
                  elo_rating: number;
                }, index: number) => (
                  <tr
                    key={entry.user_id}
                    className={`border-t border-shark-700 ${
                      index < 3 ? 'bg-shark-700/50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span
                        className={`font-bold ${
                          entry.rank === 1
                            ? 'text-yellow-400'
                            : entry.rank === 2
                            ? 'text-gray-300'
                            : entry.rank === 3
                            ? 'text-orange-400'
                            : ''
                        }`}
                      >
                        #{entry.rank}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-shark-600 rounded-full flex items-center justify-center">
                          {entry.display_name?.[0] || entry.username?.[0]}
                        </div>
                        <span>{entry.display_name || entry.username}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">{entry.games_played}</td>
                    <td className="px-6 py-4 text-center">{entry.games_won}</td>
                    <td className="px-6 py-4 text-right font-bold text-shark-400">
                      {entry.elo_rating}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-gray-400">
              No entries yet for this period
            </div>
          )}
        </div>

        {/* User Rank */}
        {data?.userRank && (
          <div className="mt-6 bg-shark-800 rounded-xl p-4">
            <h3 className="text-lg font-semibold mb-2">Your Ranking</h3>
            <div className="flex justify-between items-center">
              <span>Rank #{data.userRank.rank}</span>
              <span className="text-shark-400 font-bold">{data.userRank.eloRating} ELO</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
